import React, { useEffect, useRef, useCallback, useState } from "react";
import api from "../../../services/api";
import { Link, useLocation, useNavigate } from "react-router-dom";
import useApiByTitle from "./apis/useApiByTitle";
import useApiByAuthor from "./apis/useApiByAuthor";
import BiographyYear from "./components/BiographyYear";
import SearchEmpty from "./components/SearchEmpty";
import BiographyMainList from "./components/BiographyMainList";

/**
 * 아카이브 > 회고록 > 목록
 */

const Biography = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [posts, setPosts] = useState([]); // 현재 화면에 보여질 전체 게시물
    const [filteredPosts, setFilteredPosts] = useState([]); // 검색된 게시물
    const [visiblePosts, setVisiblePosts] = useState([]); // 화면에 표시할 데이터
    const dataPerPage = 10;
    const [isLoading, setIsLoading] = useState(false);

    const [searchTerm, setSearchTerm] = useState(""); // 검색어
    const [searchFilter, setSearchFilter] = useState("author"); // 검색 기준

    const [isQueryYear, setIsQueryYear] = useState(true);
    const [paramSelectedYear, setParamSelectedYear] = useState(null); // 검색 연도

    // 스크롤을 위한 ref (테이블 컨테이너에 적용)
    const scrollTableRef = useRef(null);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [hasMore, setHasMore] = useState(true);

    const { fetchBiographyByAuthor } = useApiByAuthor();
    const { fetchBiographyByTitle } = useApiByTitle();

    const yearRange = Array.from({ length: new Date().getFullYear() - 1800 }, (_, i) => 1800 + i);
    let propYearArray = useRef([]);

    const sortItems = (items) => {
        return [...items].sort((a, b) => {
            const valueA = a.publish_year ? parseInt(a.publish_year, 10) : 0;
            const valueB = b.publish_year ? parseInt(b.publish_year, 10) : 0;
            return valueA - valueB;
        });
    };

    // 전체 회고록 목록
    const fetchBiographyBooks = async () => {
        setIsLoading(true);
        try {
            const response = await api.post('/get_books_by_category_and_publish_year_range', {
                book_category: "biography",
                publish_years: yearRange
            });
            const formattedData = response.data.data.map((book) => ({
                book_id: book.book_id,
                authors: book.authors,
                book_title: book.book_title,
                publish_year: book.publish_year,
                publisher: book.publisher,
                library: book.library,
                n_tocs: book.n_tocs,
            }));
            if (response.status === 200 && response.data) {
                propYearArray.current = response.data.data;
                const sortedData = sortItems(formattedData);
                setPosts(sortedData);
                setFilteredPosts(sortedData);
                setVisiblePosts(sortedData.slice(0, dataPerPage));
                setHasMore(sortedData.length > dataPerPage);
            }
        } catch (error) {
            console.error(error);
        } finally {
            setIsLoading(false);
        }
    };

    // 연도별 회고록 목록
    const fetchFilteredBooks = useCallback(async (year) => {
        setIsLoading(true);
        if (!year) return;

        try {
            const response = await api.post('/get_books_by_category_and_publish_year_range', {
                book_category: "biography",
                publish_years: [year]
            });
            const formattedData = response.data.data.map((book) => ({
                book_id: book.book_id,
                authors: book.authors,
                book_title: book.book_title,
                publish_year: book.publish_year,
                publisher: book.publisher,
                library: book.library,
                n_tocs: book.n_tocs,
            }));
            if (response.status === 200 && response.data) {
                const sortedData = sortItems(formattedData);
                setPosts(sortedData);
                setFilteredPosts(sortedData);
                setVisiblePosts(sortedData.slice(0, dataPerPage));
                setHasMore(sortedData.length > dataPerPage);
            }
        } catch (error) {
            console.error(error);
        } finally {
            setIsLoading(false);
        }
    }, [dataPerPage]);

    const handleURLSearch = useCallback(async (term, filter, year) => {
        setIsLoading(true);
        if (term) {
            setParamSelectedYear(null);
            try {
                let data = [];
                if (filter === "title") {
                    data = await fetchBiographyByTitle(term);
                } else if (filter === "author") {
                    data = await fetchBiographyByAuthor(term);
                }
                const sortedData = sortItems(data);
                setPosts(sortedData);
                setFilteredPosts(sortedData);
                setVisiblePosts(sortedData.slice(0, dataPerPage));
                setHasMore(sortedData.length > dataPerPage);
            } catch (error) {
                console.error(error);
            } finally {
                setIsLoading(false);
            }
        } else if (year) {
            fetchFilteredBooks(year);
        }
    }, [fetchBiographyByTitle, fetchBiographyByAuthor, fetchFilteredBooks]);

    // URL 파라미터에 있는 검색어와 검색 기준으로 상태 설정
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const term = params.get('search') || ''; // URL에서 'search' 파라미터 추출
        const filter = params.get('filter') || 'author'; // URL에서 'filter' 파라미터 추출
        const year = params.get('year'); // URL에서 'year' 파라미터 추출

        setSearchTerm(term); // term을 state로 설정
        setSearchFilter(filter); // filter를 state로 설정
        setParamSelectedYear(year); // year를 state로 설정

        if (term.length > 0) {
            setIsQueryYear(false);
            handleURLSearch(term, filter, null);
        } else {
            setIsQueryYear(true);
            if (year) {
                // year가 있을 때는 fetchFilteredBooks 호출 전에 propYearArray 확인
                if (propYearArray.current.length === 0) {
                    fetchBiographyBooks().then(() => {
                        fetchFilteredBooks(year);
                    });
                } else {
                    fetchFilteredBooks(year);
                }
            } else {
                // year가 없을 때는 fetchBiographyBooks만 호출
                fetchBiographyBooks();
            }
        }
        setCurrentIndex(10);

        // 렌더링 될 때 마다 스크롤 초기화
        if (scrollTableRef.current) {
            scrollTableRef.current.scrollTop = 0;
        }
    }, [location.search]); // URL 파라미터가 변경될 때마다 실행

    const loadNextPage = useCallback(() => {
        if (isLoading || !hasMore) return;

        setIsLoading(true);
        const newItems = posts.slice(currentIndex, currentIndex + 10);
        setVisiblePosts(prev => [...prev, ...newItems]);
        setCurrentIndex(prev => prev + 10);

        if (currentIndex + 10 >= posts.length) {
            setHasMore(false);
        }

        setIsLoading(false);
    }, [posts, currentIndex, isLoading, hasMore]);

    // PC 환경 - 특정 스크롤 컨테이너에서 감지
    const handleScroll = useCallback(() => {
        const element = scrollTableRef.current;
        if (!element) return;

        const { scrollTop, clientHeight, scrollHeight } = element;
        if (scrollHeight - scrollTop - clientHeight <= 50) {
            loadNextPage();
        }
    }, [loadNextPage]);

    // 모바일 환경 - window.scroll 감지
    const handleWindowScroll = useCallback(() => {
        if (window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 50) {
            loadNextPage();
        }
    }, [loadNextPage]);


    useEffect(() => {
        if (window.innerWidth <= 768) {
            // 📌 모바일 환경: window 스크롤 이벤트 추가
            window.addEventListener("scroll", handleWindowScroll);
            return () => window.removeEventListener("scroll", handleWindowScroll);
        } else {
            // 📌 PC 환경: 특정 div 스크롤 이벤트 추가
            const scrollElement = scrollTableRef.current;
            if (scrollElement) {
                scrollElement.addEventListener("scroll", handleScroll);
            }
            return () => {
                if (scrollElement) {
                    scrollElement.removeEventListener("scroll", handleScroll);
                }
            };
        }
    }, [handleScroll, handleWindowScroll]);

    // 검색 기능
    const handleSearch = useCallback((searchTerm, filter) => {

        // 검색어와 기준을 URL에 파라미터로 추가
        navigate(`?search=${searchTerm}&filter=${filter}`);
    }, [navigate]);

    const handleSearchBtn = () => {
        if (!searchTerm.trim()) return;
        handleSearch(searchTerm, searchFilter);
    };

    const handleKeyDown = (e) => {
        if (e.key === "Enter") {
            e.preventDefault(); // 폼 제출 방지
            handleSearch(searchTerm, searchFilter);
        }
    };

    const handleSearchYear = useCallback((year) => {
        if (year === null) return;

        navigate(`?year=${year}`);
    }, [navigate]);

    return (
        <main id="contents">
            <div className="sub-content">
                <div className="archive-wrap">
                    <div className="cont-top">
                        <div className="w-set">
                            <div className="title-wrap">
                                <h2 className="title batang">회고록 목록</h2>
                                <p className="text">
                                    과거와 현재를 이어주는 회고록을 들여다보며,<br />우리네의 삶 속에서 겪었던 다양한 교육적 경험을 확인할 수 있습니다.
                                </p>
                            </div>
                        </div>
                    </div>
                    <div className="cont-btm">
                        <div className="archive-text-box">
                            <div className="w-set">
                                <BiographyMainList posts={posts}/>
                            </div>
                        </div>
                        <div className="list-wrap">
                            <div className="w-set">
                                <div className="search-wrap">
                                    <form onSubmit={(e) => e.preventDefault()}>
                                        <div className="search-box">
                                            <div className="select-area">
                                                <select
                                                    value={searchFilter} // 상태 값 연결
                                                    onChange={(e) => setSearchFilter(e.target.value)} // 선택 시 상태 업데이트
                                                >
                                                    <option value="author">저자</option>
                                                    <option value="title">제목</option>
                                                </select>
                                            </div>
                                            <div className="search-area">
                                                <div className="input-wrap">
                                                    <label htmlFor="">
                                                        <input
                                                            type="text"
                                                            name="search"
                                                            placeholder="검색어를 입력해 주세요."
                                                            onChange={(e) => setSearchTerm(e.target.value)}
                                                            onKeyDown={handleKeyDown}
                                                        />
                                                    </label>
                                                    <button type="button" className="btn-search" onClick={handleSearchBtn}>
                                                        검색
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </form>
                                </div>
                                <div className="list-btm">
                                    <div className="table-wrap">
                                        {filteredPosts.length === 0 && !isLoading ? (
                                            <SearchEmpty />
                                        ) : (
                                            <>
                                                {
                                                    isQueryYear ? <BiographyYear
                                                        paramSelectedYear={paramSelectedYear}
                                                        propYearArray={propYearArray.current}
                                                        handleSearchYear={handleSearchYear}
                                                    /> : ''
                                                }

                                                <div className="scroll-table" ref={scrollTableRef}>
                                                    {
                                                        visiblePosts.map((item, index) => (
                                                            <div className="list-row" key={index}>
                                                                <Link
                                                                    to={`/archive/biography/${item.book_id}`}
                                                                    className={"row-link" + (item.n_tocs !== 0 ? '' : ' disabled')}>
                                                                    <div className="title bold">{item.book_title}</div>
                                                                    <div>{item.authors}</div>
                                                                    <div>{item.publish_year}</div>
                                                                    <div>{item.publisher}</div>
                                                                    <div>{item.library}</div>
                                                                </Link>
                                                            </div>
                                                        ))
                                                    }
                                                </div>
                                            </>
                                        )}
                                    </div>
                                    {/* 로딩 인디케이터 */}
                                    <div className="load-wrap type-full" style={{ display: isLoading ? 'block' : 'none' }}>
                                        <div className="load-box">
                                            <div className="square"></div>
                                            <div className="square"></div>
                                            <div className="square"></div>
                                            <div className="square"></div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </main>
    );
};

export default Biography;
